import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import s from './styles/ApplicationDetailsPage.module.scss'
import { useApplicationDetailsPage } from './hooks/useApplicationDetailsPage'
import { refreshAppAssignments } from '../ApplicationsPage/utils/refreshAppAssignments'
import { useHeaderLeading } from 'shared/hooks/useHeaderLeading'
import { AppDetailIcon } from './icons/AppDetailIcon'
import AssignActivityModal from 'widgets/AssignActivityModal/AssignActivityModal'
import SendMessageModal from 'pages/Students/components/SendMessageModal/SendMessageModal'
import ApplicationEssayReviewDrawer from 'pages/ApplicationsPage/components/ApplicationEssayReviewDrawer/ApplicationEssayReviewDrawer'
import { useApplicationEssayReviewDrawer } from 'pages/ApplicationsPage/hooks/useApplicationEssayReviewDrawer'
import { buildDrawerApplicationRow, toDrawerEssayRow } from './utils/applicationDetailsEssays'
import { applicationDocumentsStore } from './model/applicationDocumentsStore'
import { buildAssignAllDocumentsPreset } from './utils/buildDocumentAssignItems'
import Hero from './components/Hero/Hero'
import Essays from './components/Essays/Essays'
import Documents from './components/Documents/Documents'
import Lor from './components/Lor/Lor'
import Requirements from './components/Requirements/Requirements'
import FooterNav from './components/FooterNav/FooterNav'

export const ApplicationDetailsPage = observer(() => {
	const {
		headerInfo,
		requirementsData,
		isUniversityLoading,
		currentApplication,
		navigateBack,
		backLabel,
		applicationStatusKey,
		applicationStatusHeading,
		sendMessageOpen,
		openSendMessage,
		closeSendMessage,
		sendMessageStudents,
		updateStatusOpen,
		toggleUpdateStatusMenu,
		handlePickCounselorStatus,
		counselorStatusOptions,
		statusSaving,
		setOpenEssayId,
	} = useApplicationDetailsPage()

	const essayReviewDrawer = useApplicationEssayReviewDrawer()

	useEffect(() => {
		if (!essayReviewDrawer.open) {
			setOpenEssayId(null)
			return
		}
		setOpenEssayId(essayReviewDrawer.activeEssayId || null)
	}, [essayReviewDrawer.open, essayReviewDrawer.activeEssayId, setOpenEssayId])
	const [assignOpen, setAssignOpen] = useState(false)
	const [applicationModalInstanceKey, setApplicationModalInstanceKey] =
		useState(0)
	const [applicationAssignmentPreset, setApplicationAssignmentPreset] =
		useState(null)
	const [applicationAssignmentType, setApplicationAssignmentType] = useState('')
	const [essaysReloadKey, setEssaysReloadKey] = useState(0)

	const headerLeading = useMemo(
		() => (
			<button
				type="button"
				className={s.headerBackButton}
				onClick={navigateBack}
			>
				<AppDetailIcon
					name="arrow_back"
					size={18}
					className={s.headerBackIcon}
				/>
				<span>{backLabel}</span>
			</button>
		),
		[navigateBack, backLabel],
	)

	useHeaderLeading(headerLeading)

	const openAssignmentModal = useCallback(({ type, preset = null }) => {
		setApplicationAssignmentType(type)
		setApplicationAssignmentPreset(preset)
		setApplicationModalInstanceKey(value => value + 1)
		setAssignOpen(true)
	}, [])

	const handleAssignDocument = useCallback(
		item => {
		openAssignmentModal({
			type: 'Document',
			preset: {
				locked: true,
				missingItems: [
					{
						id: item.id,
						requirementId: item.requirementId || item.id,
							title: item.name || item.title,
						action: 'upload_new',
					},
				],
				uploadedItems: [],
			},
		})
		},
		[openAssignmentModal],
	)

	const handleReuploadDocument = useCallback(
		item => {
			openAssignmentModal({
				type: 'Document',
				preset: {
					locked: true,
					missingItems: [],
					uploadedItems: [
						{
							id: item.id,
							requirementId: item.requirementId || item.id,
							title: item.name || item.title,
							action: 'reupload',
						},
					],
				},
			})
		},
		[openAssignmentModal],
	)

	const handleAssignAllDocuments = useCallback(() => {
		openAssignmentModal({
			type: 'Document',
			preset: buildAssignAllDocumentsPreset(applicationDocumentsStore.groups),
		})
	}, [openAssignmentModal])

	const handleAssignEssay = useCallback(() => {
		openAssignmentModal({ type: 'Essay' })
	}, [openAssignmentModal])

	const handleAssignLor = useCallback(() => {
		openAssignmentModal({ type: 'LoR' })
	}, [openAssignmentModal])

	const handleAssignAssignment = useCallback(() => {
		openAssignmentModal({ type: '' })
	}, [openAssignmentModal])

	const handleAssignSuccess = useCallback(async () => {
		await refreshAppAssignments()
		await applicationDocumentsStore.reload()
		setEssaysReloadKey(value => value + 1)
	}, [])

	const openEssayDrawer = useCallback(
		(essay, displayEssays) => {
			if (!essay?.id || !currentApplication) return
			essayReviewDrawer.openDrawer(
				buildDrawerApplicationRow(currentApplication, displayEssays || []),
				toDrawerEssayRow(essay),
			)
		},
		[currentApplication, essayReviewDrawer],
	)

	const documentMissingItems = applicationDocumentsStore.missingItemsForAssign
	const documentUploadedItems = applicationDocumentsStore.uploadedItemsForAssign

	if (!currentApplication) return null

	return (
		<div className={s.page}>
			<Hero
				application={currentApplication}
				headerInfo={headerInfo}
				isUniversityLoading={isUniversityLoading}
				applicationStatusKey={applicationStatusKey}
				applicationStatusHeading={applicationStatusHeading}
				onOpenSendMessage={openSendMessage}
				onAssignAssignment={handleAssignAssignment}
				statusMenuOpen={updateStatusOpen}
				onToggleStatusMenu={toggleUpdateStatusMenu}
				onPickStatus={handlePickCounselorStatus}
				counselorStatusOptions={counselorStatusOptions}
				statusSaving={statusSaving}
			/>

			<div className={s.panelsGrid}>
				<Essays
					application={currentApplication}
					reloadKey={essaysReloadKey}
					onOpenEssay={openEssayDrawer}
					onAssignEssay={handleAssignEssay}
				/>
				<Lor
					application={currentApplication}
					onAssignLor={handleAssignLor}
				/>
				<Documents
					application={currentApplication}
					onAssignDocument={handleAssignDocument}
					onReuploadDocument={handleReuploadDocument}
					onAssignAllDocuments={handleAssignAllDocuments}
				/>
			</div>

			<Requirements
				application={currentApplication}
				headerInfo={headerInfo}
				requirementsData={requirementsData}
				isUniversityLoading={isUniversityLoading}
				applicationStatusHeading={applicationStatusHeading}
			/>

			<FooterNav application={currentApplication} />

			<AssignActivityModal
				isOpen={assignOpen}
				onClose={() => {
					setAssignOpen(false)
					setApplicationAssignmentPreset(null)
					setApplicationAssignmentType('')
				}}
				selectedStudents={sendMessageStudents}
				initialUniversityId={currentApplication.universityId || ''}
				initialUniversityName={
					headerInfo.universityName || currentApplication.university || ''
				}
				initialUniversityLogoUrl={headerInfo.logoUrl || ''}
				initialApplicationTargetId={
					currentApplication.applicationTargetId || currentApplication.id || ''
				}
				tepeModal="applications"
				initialAssignmentType={applicationAssignmentType}
				applicationDocumentPreset={applicationAssignmentPreset}
				applicationModalInstanceKey={applicationModalInstanceKey}
				missingItems={documentMissingItems}
				uploadedItems={documentUploadedItems}
				clearSelectionOnOpen={false}
				lockRecipients
				onSuccess={handleAssignSuccess}
			/>
			<SendMessageModal
				isOpen={sendMessageOpen}
				onClose={closeSendMessage}
				selectedStudents={sendMessageStudents}
				selectedParents={[]}
			/>
			<ApplicationEssayReviewDrawer controller={essayReviewDrawer} />
		</div>
	)
})
